import { useCallback } from 'react'
import { useAuth, useCachedFetch, mutateCachedFetch } from '@structural-climbing/auth'
import type { NewsItem } from '@structural-climbing/ui'

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export function isNewsNew(date: string): boolean {
  return Date.now() - new Date(date).getTime() <= SEVEN_DAYS_MS
}

export interface NewsFeed {
  gym: NewsItem[]
  global: NewsItem[]
}

const EMPTY_FEED: NewsFeed = { gym: [], global: [] }

export function newsCacheKey(gymId: string | null | undefined): string | null {
  return gymId ? `news:${gymId}` : null
}

interface NewsResult extends NewsFeed {
  loading: boolean
  joinEvent: (id: string) => Promise<{ ok: boolean; full?: boolean }>
  leaveEvent: (id: string) => Promise<void>
}

// Fetches the gym + global feed once per gym. The sidebar badge, the Events page
// and the News page all call this with the same gym, so the shared cache gives
// them one request and — more importantly — one state: joining an event on the
// Events page clears the sidebar bullet without a reload.
export function useNews(gymId: string | null | undefined): NewsResult {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const key = newsCacheKey(gymId)

  const { data, loading } = useCachedFetch<NewsFeed>(key, async () => {
    const token = await getAccessTokenSilently()
    const res = await fetch(`${apiUrl}/news/${gymId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Failed to load news')
    const body = (await res.json()) as Partial<NewsFeed>
    // Normalise at the boundary so every consumer can rely on two arrays being
    // there, whatever the response turned out to be.
    return {
      gym: Array.isArray(body.gym) ? body.gym : [],
      global: Array.isArray(body.global) ? body.global : [],
    }
  })

  // Patch the shared snapshot with the count the server just returned, so every
  // caller re-renders. New arrays and objects throughout: subscribers compare by
  // reference.
  const applyCount = useCallback(
    (id: string, joined: boolean, count: number) => {
      if (!key) return
      mutateCachedFetch<NewsFeed>(key, (prev) => ({
        ...prev,
        gym: prev.gym.map((i) => (i.id === id ? { ...i, joined, signup_count: count } : i)),
      }))
    },
    [key],
  )

  const joinEvent = useCallback(
    async (id: string): Promise<{ ok: boolean; full?: boolean }> => {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/news/${id}/join`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.status === 409) return { ok: false, full: true }
        if (!res.ok) return { ok: false }
        const body = (await res.json()) as { count: number }
        applyCount(id, true, body.count)
        return { ok: true }
      } catch {
        return { ok: false }
      }
    },
    [apiUrl, getAccessTokenSilently, applyCount],
  )

  const leaveEvent = useCallback(
    async (id: string): Promise<void> => {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/news/${id}/join`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const body = (await res.json()) as { count: number }
        applyCount(id, false, body.count)
      } catch {
        // ignore — the UI stays in its current state
      }
    },
    [apiUrl, getAccessTokenSilently, applyCount],
  )

  const feed = data ?? EMPTY_FEED
  return { gym: feed.gym, global: feed.global, loading, joinEvent, leaveEvent }
}
